import type { DialogRef } from '@angular/cdk/dialog';
import { hasModifierKey } from '@angular/cdk/keycodes';
import { Injectable, Injector } from '@angular/core';
import type { Subscription } from 'rxjs';
import { BehaviorSubject, filter, take } from 'rxjs';
import type { PalantirModalAnimatedContainerComponent } from './components/modal-animated-container/modal-animated-container.component';
import { modalLogger } from './logger.instance';

const logger = modalLogger.extend('ref');

/**
 * Serviço de referência para modais Palantir.
 * Gerencia o ciclo de vida do modal, incluindo fechamento, confirmação, animações e resultado.
 * Permite integração com serviços de confirmação e manipulação de eventos de teclado e backdrop.
 */
@Injectable()
export class PalantirModalRef {
  readonly #escapeKey = 'Escape';

  /**
   * Função opcional para obter o resultado ao fechar o modal.
   */
  public getCloseResult?: () => Promise<unknown> | unknown;

  /**
   * Observable que indica se o modal foi fechado.
   */
  private modalClosed$ = new BehaviorSubject<boolean>(false);

  /**
   * Resultado do modal após fechamento.
   */
  private result: unknown = undefined;

  /**
   * Referência ao DialogRef do Angular CDK.
   */
  private _dialogRef?: DialogRef<any>;

  /**
   * Subscription para clique no backdrop.
   */
  private backdropClickSubscription?: Subscription;

  /**
   * Subscription para eventos de teclado.
   */
  private keydownEventsSubscription?: Subscription;

  /**
   * Indica se o modal está em processo de fechamento.
   */
  private isClosing = false;

  private debugId?: string | null;

  /**
   * Injeta dependências necessárias.
   */
  public constructor(private readonly injector?: Injector) {}

  public setDebugId(debugId: string | null): void {
    this.debugId = debugId;
  }

  /**
   * Fecha o modal, executando validações e animações.
   *
   * Pode exibir diálogo de confirmação se houver alterações não salvas.
   *
   */
  public async close(result?: unknown): Promise<void> {
    if (this.isClosing) {
      return;
    }

    this.isClosing = true;

    if (this.dialogRef?.config?.data?.canClose) {
      logger.debug(`Checking if modal can be closed ${this.debugId}`);
      const canClose = await this.dialogRef.config?.data?.canClose();
      logger.debug(`Modal can be closed result ${this.debugId}`, canClose);
      if (!canClose) {
        logger.info(`Modal cannot be closed ${this.debugId}`);
        this.isClosing = false;
        return;
      }
    }

    if (this.dialogRef?.config?.data?.confirmation) {
      logger.debug(`Checking if modal should be confirmed ${this.debugId}`);
      const confirmation = this.dialogRef.config.data.confirmation;
      const componentInstance = this.dialogRef.componentInstance;

      if (confirmation.evaluate(componentInstance)) {
        let confirmed = false;

        if (confirmation.confirm) {
          confirmed = await confirmation.confirm();
        } else if (this.injector) {
          logger.debug(`Opening confirm dialog (DebugId: ${this.debugId})`);

          // Lazy import to avoid circular dependency
          const { PalantirConfirmDialogService } =
            await import('../confirm-dialog/confirm-dialog.service');
          const confirmService = this.injector.get(PalantirConfirmDialogService);
          const confirmDialogRef = confirmService.openDialog({
            title: 'Alterações não salvas',
            text: 'Você possui alterações não salvas. Tem certeza que deseja fechar?',
            successText: 'Descartar alterações',
            cancelText: 'Continuar editando',
            cancelVariant: 'neutral',
            successVariant: 'negative',
            reverseButtons: true,
          });

          const result = await confirmDialogRef.afterClosed();
          logger.debug(`Confirm dialog result (DebugId: ${this.debugId})`, result);
          confirmed = result?.role === 'success';
        }

        if (!confirmed) {
          logger.info(`Modal cannot confirm (DebugId: ${this.debugId})`);
          this.isClosing = false;
          return;
        }
      }
    }

    const containerInstance = this.dialogRef
      ?.containerInstance as PalantirModalAnimatedContainerComponent;

    if (!containerInstance) {
      this.dialogRef?.close(result);
      this.modalClosed$.next(true);
      this.isClosing = false;
      return;
    }

    containerInstance.animationStateChanged
      .pipe(
        filter((animationEvent) => animationEvent.phaseName === 'start'),
        take(1),
      )
      .subscribe(() => {
        logger.debug(`Modal animation started (DebugId: ${this.debugId})`);
        this.dialogRef?.overlayRef.detachBackdrop();
      });

    containerInstance.animationStateChanged
      .pipe(
        filter(
          (animationEvent) =>
            animationEvent.phaseName === 'done' && animationEvent.toState === 'closed',
        ),
        take(1),
      )
      .subscribe(async () => {
        // eslint-disable-next-line require-atomic-updates, no-param-reassign
        result = result ?? (await this.getCloseResult?.());

        logger.debug(`Modal closed (DebugId: ${this.debugId})`, result);
        this.result = result;
        this.dialogRef?.close(result);
        this.modalClosed$.next(true);
        this.isClosing = false;
      });

    containerInstance.startExitAnimation();
  }

  /**
   * Retorna uma Promise resolvida quando o modal for fechado.
   *
   * @returns Promise com o resultado do modal.
   */
  public async afterClosed<T>(): Promise<T> {
    return new Promise((resolve) => {
      const modalClosed = this.modalClosed$.subscribe((closed) => {
        if (!closed) {
          return;
        }

        logger.debug(`Modal closed (DebugId: ${this.debugId})`, this.result);
        resolve(this.result as T);
        modalClosed.unsubscribe();
      });
    });
  }

  /**
   * Getter para o DialogRef do modal.
   */
  public get dialogRef(): DialogRef<any> | undefined {
    return this._dialogRef;
  }

  /**
   * Setter para o DialogRef do modal.
   * Gerencia subscriptions para eventos de clique no backdrop e teclado.
   */
  public set dialogRef(dialogRef: DialogRef<any>) {
    this.backdropClickSubscription?.unsubscribe();
    this.keydownEventsSubscription?.unsubscribe();

    this._dialogRef = dialogRef;

    this.backdropClickSubscription = this._dialogRef.backdropClick.subscribe(async () => {
      await this.close();
    });

    this.keydownEventsSubscription = this._dialogRef.keydownEvents
      .pipe(
        filter((event) => event.key === this.#escapeKey && !hasModifierKey(event)),
        take(1),
      )
      .subscribe(async (event: KeyboardEvent) => {
        event.preventDefault();
        await this.close();
      });
  }
}
